import React from 'react';
import styled from 'styled-components/macro';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ResponseError } from 'superagent';
import { ApiClient } from 'src/client/helpers';
import { useJoinUrlGenerator } from 'src/client/components/buttons/JoinButton';
import { UPGRADE_STRIP, UPGRADE_STRIP_MODAL } from 'src/client/constants/joinCampaigns';
import { AdvancedModal, ModalBackdrop, ModalBody, ModalDialog } from 'src/client/components/ui/AdvancedModal';
import urls, { constructUrl } from 'src/shared/urls';
import { MOBILE_LANDSCAPE, TABLET_LANDSCAPE, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { useSelector } from 'src/client/redux';
import { PlanCards } from './PlanCards';
import type { UpgradeProductListing, UpgradesResponse } from './types';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';

import {
  MetArt,
  SexArt,
  MetArtX,
  VivThomas,
  TheLifeErotic,
  Straplez,
  ErroticaArchives,
  EroticBeauty,
  Domai,
  GoddessNudes,
  MetArtNetwork,
} from 'src/client/constants/sites';

type Props = {
  onHide: () => void;
  show: boolean;
};

const upgradesFetcher = (params: Partial<{ affiliateID: string; promoCode: string }>) =>
  new ApiClient().get(constructUrl(urls.get.upgrades), { params }).then((res) => res.body as UpgradesResponse);

const sitesAsTags = [
  MetArt,
  SexArt,
  MetArtX,
  VivThomas,
  TheLifeErotic,
  Straplez,
  ErroticaArchives,
  EroticBeauty,
  Domai,
  GoddessNudes,
];

export const MetartNetworkModal = (props: Props) => {
  const { onHide, show } = props;
  const { search } = useLocation();
  const generateJoinUrl = useJoinUrlGenerator();
  const { cdnUrl, joinSubdomain } = useSelector((state) => state.app);
  const userEmail = useSelector((state) => state.auth.user?.email);
  const showSfwSite = useShowSfwSite();

  const searchParams = new URLSearchParams(search);
  const extraParams = {
    affiliateID: searchParams.get('affiliateID') || searchParams.get('PA') || '',
    promoCode: searchParams.get('promoCode') || '',
  };

  const upgradesQuery = useQuery<UpgradesResponse, ResponseError>({
    enabled: show,
    queryFn: () => upgradesFetcher(extraParams),
    queryKey: ['metart-network-upgrades', extraParams],
    staleTime: 5 * 60 * 1000,
  });

  const networkUpgrade =
    upgradesQuery.data?.upgrades.find((upgrade) => upgrade.abbreviation === MetArtNetwork.abbreviation) || null;

  const defaultJoinUrl = generateJoinUrl({
    campaign: UPGRADE_STRIP,
    extraParams,
    overrideSite: MetArtNetwork.abbreviation,
  });
  const billingPortalDomain = networkUpgrade?.domain?.toLowerCase() || 'metartnetwork.com';
  const priceGroupUUID = upgradesQuery.data?.priceGroupUUID;
  const txnID = upgradesQuery.data?.txnID;

  const getPlanUrl = (product: UpgradeProductListing) => {
    if (!userEmail || !txnID || !priceGroupUUID) {
      return defaultJoinUrl;
    }

    const params = new URLSearchParams({
      campaign: UPGRADE_STRIP_MODAL,
      email: userEmail,
      initialPrice: product.initialPrice,
      priceGroupUUID,
      txnID,
    });

    return `https://${joinSubdomain}.${billingPortalDomain}/external/landing?${params.toString()}`;
  };

  const siteThumbnail = showSfwSite ? `${cdnUrl}/img/man-upgrade-modal-sfw.jpg` : `${cdnUrl}/img/man-upgrade-modal.jpg`;

  return (
    <AdvancedModal
      BackdropComponent={StyledBackdrop}
      BodyComponent={StyledBody}
      DialogComponent={StyledDialog}
      lockBackgroundScroll
      onClose={onHide}
      show={show}
    >
      <ModalContent>
        <Header>
          <SitePicture src={siteThumbnail} />
          <SiteInfo>
            <SiteTitle>Metart Network</SiteTitle>
            {networkUpgrade?.description ? <SiteDescription>{networkUpgrade.description}</SiteDescription> : null}
            <SiteTags>
              {sitesAsTags.map((site) => (
                <Tag key={site.abbreviation}>{site.name}</Tag>
              ))}
            </SiteTags>
          </SiteInfo>
        </Header>

        {upgradesQuery.isLoading && <StatusPanel>Loading current upgrade offers...</StatusPanel>}

        {upgradesQuery.isError && (
          <StatusPanel>
            Unable to load upgrade offers right now.
            <StatusLink href={defaultJoinUrl} onClick={onHide}>
              Open the discounts area instead
            </StatusLink>
          </StatusPanel>
        )}

        {!upgradesQuery.isLoading && !upgradesQuery.isError && !networkUpgrade && (
          <StatusPanel>
            No MetArt Network plans are available right now.
            <StatusLink href={defaultJoinUrl} onClick={onHide}>
              Open the discounts area
            </StatusLink>
          </StatusPanel>
        )}

        {networkUpgrade && (
          <>
            <SelectorTitle>Choose Your Plan</SelectorTitle>
            <PlanCards getPlanUrl={getPlanUrl} onHide={onHide} products={networkUpgrade.products} />
          </>
        )}
      </ModalContent>
    </AdvancedModal>
  );
};

const StyledBackdrop = styled(ModalBackdrop)`
  background-color: rgba(0, 0, 0, 0.6);
`;

const StyledDialog = styled(ModalDialog)`
  max-height: 90vh;
  max-width: 90vw;
  width: 90vw;
  border-radius: 0;
  border-color: ${(props) => props.theme.primary2};

  @media ${MOBILE_LANDSCAPE} {
    max-width: min(92vw, 720px);
    width: min(92vw, 720px);
  }

  @media ${TABLET_PORTRAIT} {
    max-height: min(88vh, 920px);
    max-width: min(96vw, 1040px);
    width: min(96vw, 1040px);
  }
`;

const StyledBody = styled(ModalBody)`
  background-color: ${(props) => props.theme.primary1};
  color: ${(props) => props.theme.primary4};
  padding: 12px;

  @media ${TABLET_PORTRAIT} {
    padding: 16px;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px;

  @media ${TABLET_PORTRAIT} {
    gap: 20px;
    padding: 10px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${TABLET_PORTRAIT} {
    align-items: flex-start;
    flex-direction: row;
    gap: 20px;
  }
`;

const SiteTitle = styled.h3`
  font-size: 28px;
  margin: 0;

  @media ${TABLET_PORTRAIT} {
    font-size: 30px;
  }
`;

const SitePicture = styled.img`
  aspect-ratio: 1 / 1;
  border: 3px solid;
  border-radius: 10px;
  display: block;
  object-fit: cover;
  margin: 0 auto;
  max-width: 220px;
  width: min(100%, 220px);
  border-color: ${(props) => props.theme.primaryBtn1Bg};

  @media ${TABLET_PORTRAIT} {
    flex: 0 0 220px;
    margin: 0;
  }
`;

const StatusPanel = styled.div`
  align-items: flex-start;
  border: 1px solid #eadf9a;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
`;

const StatusLink = styled.a`
  font-weight: 700;
  text-decoration: underline;
`;

const SiteInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  text-align: center;

  @media ${TABLET_PORTRAIT} {
    gap: 20px;
    text-align: left;
  }
`;

const SiteDescription = styled.p`
  font-size: 16px;
  margin: 0;
`;

const SiteTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;

  @media ${TABLET_PORTRAIT} {
    justify-content: flex-start;
  }
`;

const Tag = styled.span`
  align-items: center;
  background-color: ${(props) => props.theme.primary2};
  border-radius: 4px;
  display: inline-flex;
  font-size: 16px;
  height: 32px;
  justify-content: center;
  padding: 0 16px;
`;

const SelectorTitle = styled.h4`
  font-size: 1.375rem;
  margin: 0;
  text-align: center;

  @media ${TABLET_PORTRAIT} {
    font-size: 1.75rem;
  }

  @media ${TABLET_LANDSCAPE} {
    font-size: 2.5rem;
  }
`;
